// src/components/Home/AboutSection.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const AboutSection: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const features = [
    {
      icon: '🌱',
      title: 'Sustainable Farming',
      description: 'Promoting eco-friendly agriculture practices',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: '👩‍🌾',
      title: 'Women Empowerment',
      description: 'Supporting female agripreneurs',
      color: 'from-purple-500 to-pink-600'
    },
    {
      icon: '🌍',
      title: 'Climate Action',
      description: 'Combating climate change impacts',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: '👨‍🎓',
      title: 'Youth Development',
      description: 'Training future green leaders',
      color: 'from-orange-500 to-red-500'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "backOut"
      }
    },
    hover: {
      scale: 1.05,
      zIndex: 10,
      transition: {
        duration: 0.3
      }
    }
  };

  const featureVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    hover: {
      y: -5,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <section ref={ref} className="py-5 bg-light position-relative overflow-hidden">
      {/* Background Elements */}
      <div className="position-absolute top-0 end-0 w-50 h-100">
        <div className="position-absolute top-0 end-0 w-100 h-100 bg-gradient-to-l from-green-50 to-transparent opacity-50"></div>
      </div>
      
      <div className="container position-relative z-2">
        <div className="row align-items-center">
          {/* Content Column */}
          <div className="col-lg-6 mb-5 mb-lg-0">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              {/* Badge */}
              <motion.div
                variants={itemVariants}
                className="d-inline-flex align-items-center px-3 py-2 rounded-pill bg-success bg-opacity-10 mb-4"
              >
                <span className="text-success fw-semibold small">🌟 Since 2018</span>
              </motion.div>

              {/* Heading */}
              <motion.h2
                variants={itemVariants}
                className="display-4 fw-black text-dark mb-4"
              >
                Creating{' '}
                <span className="text-success position-relative">
                  Sustainable
                  <motion.span
                    className="position-absolute bottom-0 start-0 w-100 h-1 bg-success"
                    initial={{ scaleX: 0 }}
                    animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                  />
                </span>{' '}
                Futures Across Africa
              </motion.h2>

              {/* Description */}
              <motion.p
                variants={itemVariants}
                className="lead text-muted mb-5 fs-5"
                style={{ lineHeight: '1.7' }}
              >
                We are a community-based organization dedicated to creating sustainable 
                food security and climate resilience across African communities through 
                innovative solutions and grassroots empowerment.
              </motion.p>

              {/* Features Grid */}
              <motion.div
                variants={containerVariants}
                className="row g-4 mb-5"
              >
                {features.map((feature) => (
                  <motion.div
                    key={feature.title}
                    className="col-sm-6"
                    variants={featureVariants}
                    whileHover="hover"
                  >
                    <div className="d-flex align-items-start p-3 rounded-3 h-100 feature-card">
                      <div className={`flex-shrink-0 me-3 p-3 rounded-3 bg-gradient-to-br ${feature.color} text-white shadow-sm`}>
                        <span style={{ fontSize: '1.5rem' }}>{feature.icon}</span>
                      </div>
                      <div className="flex-grow-1">
                        <h5 className="fw-bold text-dark mb-2">{feature.title}</h5>
                        <p className="text-muted small mb-0">{feature.description}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* CTA Button */}
              <motion.div
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link 
                  to="/about" 
                  className="btn btn-success btn-lg px-5 py-3 fw-bold shadow-lg position-relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #198754 0%, #146c43 100%)',
                    border: 'none',
                    borderRadius: '12px'
                  }}
                >
                  <span className="position-relative z-2">
                    Discover Our Mission  →
                  </span>
                  <motion.div
                    className="position-absolute top-0 left-0 w-100 h-100 bg-white bg-opacity-20"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: '100%' }}
                    transition={{ duration: 0.6 }}
                  />
                </Link>
              </motion.div>
            </motion.div>
          </div>

          {/* Images Column */}
          <div className="col-lg-6">
            <motion.div
              className="position-relative"
              variants={containerVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              {/* Main Image Grid */}
              <div className="row g-4">
                <div className="col-6">
                  <motion.div
                    variants={imageVariants}
                    whileHover="hover"
                    className="position-relative rounded-4 overflow-hidden shadow-lg"
                    style={{ height: '280px' }}
                  >
                    <img 
                      src="/images/about-1.jpeg" 
                      alt="Community farming"
                      className="img-fluid w-100 h-100 object-cover"
                    />
                    <div className="position-absolute bottom-0 start-0 w-100 p-3 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-green mb-0 small fw-semibold">Community Farming</p>
                    </div>
                  </motion.div>
                </div>
                
                <div className="col-6">
                  <motion.div
                    variants={imageVariants}
                    whileHover="hover"
                    className="position-relative rounded-4 overflow-hidden shadow-lg"
                    style={{ height: '350px', marginTop: '50px' }}
                  >
                    <img 
                      src="/images/about-2.jpeg" 
                      alt="Women empowerment"
                      className="img-fluid w-100 h-100 object-cover"
                    />
                    <div className="position-absolute bottom-0 start-0 w-100 p-3 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-black mb-0 small fw-semibold">Women Empowerment</p>
                    </div>
                  </motion.div>
                </div>
                
                <div className="col-6">
                  <motion.div
                    variants={imageVariants}
                    whileHover="hover"
                    className="position-relative rounded-4 overflow-hidden shadow-lg"
                    style={{ height: '350px' }}
                  >
                    <img 
                      src="/images/about-3.jpeg" 
                      alt="Tree planting"
                      className="img-fluid w-100 h-100 object-cover"
                    />
                    <div className="position-absolute bottom-0 start-0 w-100 p-3 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-black mb-0 small fw-semibold">Climate Action</p>
                    </div>
                  </motion.div>
                </div>
                
                <div className="col-6">
                  <motion.div
                    variants={imageVariants}
                    whileHover="hover"
                    className="position-relative rounded-4 overflow-hidden shadow-lg"
                    style={{ height: '280px', marginTop: '-30px' }}
                  >
                    <img 
                      src="/images/about-4.jpeg" 
                      alt="Youth training"
                      className="img-fluid w-100 h-100 object-cover"
                    />
                    <div className="position-absolute bottom-0 start-0 w-100 p-3 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-black mb-0 small fw-semibold">Youth Development</p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style>{`
        .feature-card {
          transition: all 0.3s ease;
          border: 1px solid transparent;
        }
        
        .feature-card:hover {
          border-color: #198754;
          background: rgba(25, 135, 84, 0.02);
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
        }
        
        .object-cover {
          object-fit: cover;
        }
        
        /* Gradient backgrounds for feature icons */
        .from-green-500.to-emerald-600 {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }
        
        .from-purple-500.to-pink-600 {
          background: linear-gradient(135deg, #8b5cf6 0%, #db2777 100%);
        }
        
        .from-blue-500.to-cyan-600 {
          background: linear-gradient(135deg, #3b82f6 0%, #0891b2 100%);
        }
        
        .from-orange-500.to-red-500 {
          background: linear-gradient(135deg, #f97316 0%, #ef4444 100%);
        }
        
        .bg-gradient-to-l {
          background: linear-gradient(to left, var(--tw-gradient-stops));
        }
        
        .bg-gradient-to-t {
          background: linear-gradient(to top, var(--tw-gradient-stops));
        }
        
        .bg-gradient-to-br {
          background: linear-gradient(to bottom right, var(--tw-gradient-stops));
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .display-4 {
            font-size: 2.5rem !important;
          }
          
          .position-absolute.floating-stats {
            position: relative !important;
            transform: none !important;
            margin-bottom: 2rem;
          }
        }
      `}</style>
    </section>
  );
};

export default AboutSection;